import React, { useEffect, useMemo, useState } from "react";
import "./css/ResultDates.css";
import {apiFetch as fetch} from "apiClient";

/**
 * Adds:
 * - Two segmented toggles (Portfolio ⟷ All Companies, Upcoming ⟷ All)
 * - Date filter (type="date"): shows only rows matching the picked date
 * - Prev / Next date buttons that move to adjacent available dates (within the current dataset)
 * - Hides Current Value in All Companies mode (Symbol + Result Date only)
 *
 * Endpoints expected:
 *   /api/result-dates/portfolio/upcoming  -> [{ row:{ stockSymbol, currentValue, ... }, resultDate }]
 *   /api/result-dates/portfolio/all       -> same shape
 *   /api/result-dates/all/upcoming        -> [{ symbol, resultDate }]
 *   /api/result-dates/all                 -> same shape
 */

const EXCLUDED_KEYS = new Set([
    "id","isin","todaysGain","todaysGainPct","overallGain","overallGainPct",
    "quantity","avgPrice","invested","price","asOf","clientName"
]);

const dtf = new Intl.DateTimeFormat(undefined, { day: "2-digit", month: "short", year: "numeric" });
const fmtDate = (iso) => {
    if (!iso) return "-";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return String(iso);
    const parts = dtf.formatToParts(d);
    const day = parts.find(p => p.type === "day")?.value ?? "";
    const mon = parts.find(p => p.type === "month")?.value ?? "";
    const yr  = parts.find(p => p.type === "year")?.value ?? "";
    return `${day} ${mon}, ${yr}`;
};

function pretty(key) {
    return String(key)
        .replace(/([A-Z])/g, " $1")
        .replace(/[_\-]+/g, " ")
        .replace(/\s+/g, " ")
        .trim()
        .replace(/\b\w/g, (c) => c.toUpperCase());
}

function SortIcon({ active, direction }) {
    if (!active) {
        return (
            <svg className="rd-sort-icon" width="12" height="12" viewBox="0 0 24 24" aria-hidden="true">
                <path d="M7 10l5-5 5 5H7zm0 4h10l-5 5-5-5z"></path>
            </svg>
        );
    }
    const d = direction === "asc" ? "M7 14l5-5 5 5H7" : "M7 10l5 5 5-5H7";
    return (
        <svg className={`rd-sort-icon rd-sort-icon--${direction}`} width="12" height="12" viewBox="0 0 24 24" aria-hidden="true">
            <path d={d}></path>
        </svg>
    );
}

export default function ResultDates() {
    const [scope, setScope] = useState("pf");       // "pf" | "all"
    const [range, setRange] = useState("upcoming"); // "upcoming" | "all"
    const [sortKey, setSortKey] = useState("resultDate"); // "resultDate" | "currentValue"
    const [sortDir, setSortDir] = useState("asc");
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Date filter (ISO yyyy-MM-dd)
    const [selectedDate, setSelectedDate] = useState(""); // e.g., "2025-10-09"

    // Local datasets (works without any external Context)
    const [pfUpcoming, setPfUpcoming] = useState([]);
    const [pfAll, setPfAll] = useState([]);
    const [allUpcoming, setAllUpcoming] = useState([]);
    const [allAll, setAllAll] = useState([]);

    const refresh = async () => {
        setLoading(true);
        setError(null);
        try {
            const [r1, r2, r3, r4] = await Promise.all([
                fetch('/api/result-dates/portfolio/upcoming'),
                fetch('/api/result-dates/portfolio/all'),
                fetch('/api/result-dates/all/upcoming'),
                fetch('/api/result-dates/all'),
            ]);

            const [a1, a2, a3, a4] = await Promise.all([
                r1.json(),
                r2.json(),
                r3.json(),
                r4.json(),
            ]);

            setPfUpcoming(Array.isArray(a1) ? a1 : []);
            setPfAll(Array.isArray(a2) ? a2 : []);
            setAllUpcoming(Array.isArray(a3) ? a3 : []);
            setAllAll(Array.isArray(a4) ? a4 : []);
        } catch (e) {
            console.error(e);
            setError(String(e?.message || e));
        } finally {
            setLoading(false);
        }
    };


    useEffect(() => { refresh(); }, []);

    // Pick the active dataset based on toggles
    const rows = useMemo(() => {
        let data;
        if (scope === "pf") data = range === "upcoming" ? pfUpcoming : pfAll;
        else data = range === "upcoming" ? allUpcoming : allAll;
        return Array.isArray(data) ? data : [];
    }, [scope, range, pfUpcoming, pfAll, allUpcoming, allAll]);

    // Unique result dates (ISO yyyy-MM-dd) from the active dataset
    const uniqueDates = useMemo(() => {
        const set = new Set(
            rows
                .map((item) => {
                    const d = item?.resultDate;
                    if (!d) return null;
                    if (typeof d === "string") return d.slice(0, 10);
                    try { return new Date(d).toISOString().slice(0,10); } catch { return null; }
                })
                .filter(Boolean)
        );
        return Array.from(set).sort(); // ascending
    }, [rows]);

    // Filter by selectedDate (if provided)
    const filteredRows = useMemo(() => {
        if (!selectedDate) return rows;
        return rows.filter((item) => {
            const d = item?.resultDate;
            if (!d) return false;
            if (typeof d === "string") return d.slice(0, 10) === selectedDate;
            const iso = new Date(d).toISOString().slice(0, 10);
            return iso === selectedDate;
        });
    }, [rows, selectedDate]);

    // Visible PF columns (All Companies shows only 2 columns)
    const visibleColumns = useMemo(() => {
        if (scope !== "pf") return [];
        const firstRow = filteredRows.find(r => r?.row) || {};
        const keys = Object.keys(firstRow.row || {});
        return keys.filter(k => k !== "stockSymbol" && !EXCLUDED_KEYS.has(k));
    }, [filteredRows, scope]);

    // Sort after filter
    const sortedRows = useMemo(() => {
        const data = [...filteredRows];
        const getVal = (item) => {
            if (sortKey === "resultDate") {
                return item.resultDate ? new Date(item.resultDate).getTime() : null;
            }
            if (sortKey === "currentValue" && scope === "pf") {
                const v = item?.row?.currentValue;
                return (typeof v === "number" && Number.isFinite(v)) ? v : (v == null ? null : Number(v));
            }
            return null;
        };
        data.sort((a, b) => {
            const A = getVal(a), B = getVal(b);
            if (A == null && B == null) return 0;
            if (A == null) return sortDir === "asc" ? 1 : -1;
            if (B == null) return sortDir === "asc" ? -1 : 1;
            if (A < B) return sortDir === "asc" ? -1 : 1;
            if (A > B) return sortDir === "asc" ? 1 : -1;
            return 0;
        });
        return data;
    }, [filteredRows, sortKey, sortDir, scope]);

    const toggleSort = (key) => {
        if (key === "currentValue" && scope !== "pf") return;
        if (sortKey === key) setSortDir(sortDir === "asc" ? "desc" : "asc");
        else { setSortKey(key); setSortDir("asc"); }
    };
    const ariaSort = (key) => (sortKey === key ? (sortDir === "asc" ? "ascending" : "descending") : "none");

    // Prev / Next date navigation
    const currentIdx = useMemo(
        () => (selectedDate ? uniqueDates.indexOf(selectedDate) : -1),
        [selectedDate, uniqueDates]
    );
    const canPrev = currentIdx > 0;
    const canNext = currentIdx >= 0 && currentIdx < uniqueDates.length - 1;

    const goPrev = () => {
        if (!canPrev) return;
        setSelectedDate(uniqueDates[currentIdx - 1]);
    };
    const goNext = () => {
        if (!canNext) return;
        setSelectedDate(uniqueDates[currentIdx + 1]);
    };

    const title = useMemo(() => {
        const left = scope === "pf" ? "Portfolio" : "All Companies";
        const right = range === "upcoming" ? "Upcoming" : "All";
        return `${left} • ${right} results`;
    }, [scope, range]);

    return (
        <div className="rd-page-shell">
            <div className="rd-card">
                <div className="rd-card-header">
                    <div className="rd-title">
                        <span className="rd-title-main">{title}</span>
                        <span className="rd-title-sub">{sortedRows.length.toLocaleString()} records</span>
                    </div>

                    <div className="rd-toolbar">
                        <div className="rd-segment">
                            <button className={`rd-seg-btn ${scope==='pf'?'is-active':''}`} onClick={()=>setScope('pf')}>Portfolio</button>
                            <button className={`rd-seg-btn ${scope==='all'?'is-active':''}`} onClick={()=>setScope('all')}>All Companies</button>
                        </div>
                        <div className="rd-segment">
                            <button className={`rd-seg-btn ${range==='upcoming'?'is-active':''}`} onClick={()=>setRange('upcoming')}>Upcoming</button>
                            <button className={`rd-seg-btn ${range==='all'?'is-active':''}`} onClick={()=>setRange('all')}>All</button>
                        </div>

                        {/* Date filter + Prev/Next */}
                        <div className="rd-filter">
                            <label className="rd-filter-label" htmlFor="rd-filter-date">On date</label>

                            <div className="rd-nav">
                                <button
                                    type="button"
                                    className="rd-nav-btn"
                                    aria-label="Previous date"
                                    title="Previous date"
                                    onClick={goPrev}
                                    disabled={!selectedDate || !canPrev}
                                >
                                    {/* left chevron */}
                                    <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden="true">
                                        <path d="M15 18l-6-6 6-6" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                </button>

                                <input
                                    id="rd-filter-date"
                                    type="date"
                                    className="rd-input-date"
                                    value={selectedDate}
                                    onChange={(e)=>setSelectedDate(e.target.value)}
                                />

                                <button
                                    type="button"
                                    className="rd-nav-btn"
                                    aria-label="Next date"
                                    title="Next date"
                                    onClick={goNext}
                                    disabled={!selectedDate || !canNext}
                                >
                                    {/* right chevron */}
                                    <svg width="14" height="14" viewBox="0 0 24 24" aria-hidden="true">
                                        <path d="M9 6l6 6-6 6" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round"/>
                                    </svg>
                                </button>

                                {selectedDate && (
                                    <button
                                        className="rd-filter-clear"
                                        title="Clear date filter"
                                        onClick={()=>setSelectedDate("")}
                                    >
                                        ×
                                    </button>
                                )}
                            </div>
                        </div>

                    </div>

                    {error && <div className="rd-error">⚠ {error}</div>}
                </div>

                <div className="rd-table-wrap">
                    <table className="rd-table">
                        <thead className="rd-thead">
                        <tr>
                            <th className="rd-th" scope="col">Symbol</th>
                            <th
                                className="rd-th rd-th--sortable"
                                scope="col"
                                role="columnheader button"
                                aria-sort={ariaSort("resultDate")}
                                onClick={() => toggleSort("resultDate")}
                                tabIndex={0}
                                onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && toggleSort("resultDate")}
                            >
                                <span className="rd-th-label">Result Date</span>
                                <SortIcon active={sortKey === "resultDate"} direction={sortDir} />
                            </th>

                            {/* PF-only additional columns (including Current Value) */}
                            {scope === "pf" && visibleColumns.map((col) => {
                                const sortable = col === "currentValue";
                                const active = sortable && sortKey === "currentValue";
                                return (
                                    <th
                                        key={col}
                                        className={`rd-th ${sortable ? "rd-th--sortable" : ""}`}
                                        scope="col"
                                        role={sortable ? "columnheader button" : "columnheader"}
                                        aria-sort={sortable ? ariaSort("currentValue") : undefined}
                                        onClick={sortable ? () => toggleSort("currentValue") : undefined}
                                        tabIndex={sortable ? 0 : -1}
                                        onKeyDown={sortable ? (e) => (e.key === "Enter" || e.key === " ") && toggleSort("currentValue") : undefined}
                                        title={col}
                                    >
                                        <span className="rd-th-label">{pretty(col)}</span>
                                        {sortable ? <SortIcon active={active} direction={sortDir} /> : null}
                                    </th>
                                );
                            })}
                        </tr>
                        </thead>

                        <tbody className="rd-tbody">
                        {sortedRows.map((item, idx) => {
                            const r = item.row || {};
                            const symbol = r.stockSymbol ?? item.symbol ?? "-";
                            return (
                                <tr key={idx} className="rd-tr">
                                    <td className="rd-td rd-td--mono">{symbol}</td>
                                    <td className="rd-td">{fmtDate(item.resultDate)}</td>
                                    {scope === "pf" && visibleColumns.map((col) => (
                                        <td key={col} className="rd-td">
                                            {(() => {
                                                const v = r[col];
                                                if (col === "currentValue") {
                                                    const num = (typeof v === "number") ? v : Number(v);
                                                    return Number.isFinite(num) ? num.toLocaleString() : (v ?? "-");
                                                }
                                                return v == null ? "-" : String(v);
                                            })()}
                                        </td>
                                    ))}
                                </tr>
                            );
                        })}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}
